import { useState } from 'react';
import { Button, Card, Flex, Stack, useTheme, spacing } from '@fti/design-system';
import type { ProjectUpdateRequest, ProjectMember, UserInfo } from '../../../types/media-sim';
import { useUsers } from '../../../api/media-sim/users';
import { HelpText } from '../shared/HelpText';
import { BillingCodeField, BILLING_CODE_REGEX } from './BillingCodeField';

interface WizardStep1Props {
  data: {
    project_name?: string;
    client_name?: string;
    billing_code?: string;
    key_facts?: string | null;
  };
  onChange: (updates: Partial<ProjectUpdateRequest>) => void;
  members: ProjectMember[];
  onAddMember: (userId: string) => void;
  onRemoveMember: (memberId: string) => void;
  errors: Record<string, string>;
}

const KEY_FACTS_EXAMPLE = `DEAL OVERVIEW
- Unilever has confirmed it is in discussions with McCormick regarding a potential sale of its food (nutrition) business
- Discussions are preliminary; no definitive agreement, deal structure, or timeline has been announced
- Transaction would likely be a multi-billion dollar divestiture given the scale of Unilever's food portfolio

ABOUT UNILEVER
- UK-based multinational consumer goods company headquartered in London. One of the largest global consumer goods companies with around +\u20AC50 billion revenue. Its Food (nutrition) division generates ~\u20AC12.9\u201313.4 billion, representing roughly ~25% of total company sales.
- CEO: Hein Schumacher
- Business segments: Beauty & Wellbeing, Personal Care, Home Care, Nutrition (food business under review)
- Key food brands include: Hellmann's (mayonnaise), Knorr (soups, seasonings), Magnum, Ben & Jerry's (ice cream)
- Has been conducting a strategic review of its food division, including potential separation or sale

ABOUT MCCORMICK & COMPANY
- U.S.-based global food company headquartered in Maryland. Annual revenue is ~$6.8\u20137 billion. Global leader in spices and seasonings.
- CEO: Brendan M. Foley
- Core business: Spices, herbs, seasonings, condiments, and flavor solutions
- Key brands: McCormick spices, French's (mustard), Frank's RedHot (hot sauce)
- Following its 2017 acquisition of French's and Frank's, became roughly the #3 global condiment player

PRIOR RELATED DEAL ACTIVITY
- Unilever previously held discussions with Kraft Heinz regarding a potential combination of food-related assets (no transaction resulted)
- Unilever has publicly explored multiple strategic options for its food business, including spin-off, partial separation, and sale to strategic buyer

INDUSTRY CONTEXT
- Ongoing consolidation in global packaged food and condiments sector
- Large consumer goods companies increasingly divesting slower-growth food assets and focusing on higher-growth categories (e.g., beauty, wellness)
- Increased investor focus on portfolio simplification and margin expansion`;

export function WizardStep1({
  data,
  onChange,
  members,
  onAddMember,
  onRemoveMember,
  errors,
}: WizardStep1Props) {
  const { theme } = useTheme();
  const [userSearch, setUserSearch] = useState('');
  const { data: users } = useUsers(userSearch || undefined);

  const [billingError, setBillingError] = useState('');

  const validateBillingCode = (value: string) => {
    if (value && !BILLING_CODE_REGEX.test(value)) {
      setBillingError('Format: 005110.0057-100');
    } else {
      setBillingError('');
    }
  };

  const inputStyle = {
    width: '100%',
    padding: `${spacing.xs} ${spacing.sm}`,
    border: `1px solid ${theme.colors.border.default}`,
    borderRadius: '4px',
    backgroundColor: theme.colors.background.paper,
    color: theme.colors.text.primary,
    fontSize: '0.9rem',
  };

  const labelStyle = {
    display: 'block' as const,
    marginBottom: spacing.xs,
    fontSize: '0.85rem',
    fontWeight: 500 as const,
    color: theme.colors.text.primary,
  };

  const errorStyle = {
    fontSize: '0.8rem',
    color: theme.colors.semantic.error.default,
    marginTop: '2px',
  };

  return (
    <Card variant="elevated">
      <Card.Body>
        <Stack gap="md">
          <h3 style={{ margin: 0, color: theme.colors.text.primary }}>Project Identity</h3>

          <div>
            <label style={labelStyle}>Project Name *</label>
            <input
              type="text"
              value={data.project_name || ''}
              onChange={(e) => onChange({ project_name: e.target.value })}
              placeholder="Enter project name"
              style={inputStyle}
            />
            {errors.project_name && <p style={errorStyle}>{errors.project_name}</p>}
          </div>

          <div>
            <label style={labelStyle}>Company *</label>
            <HelpText>
              Input the name of the company releasing the messaging or that is the intended focus of coverage.
              Use the full name, not acronyms (e.g., "FTI Consulting", "Exxon Mobil", "Alphabet/Google").
            </HelpText>
            <input
              type="text"
              value={data.client_name || ''}
              onChange={(e) => onChange({ client_name: e.target.value })}
              placeholder="Enter company name"
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>Billing Code *</label>
            <BillingCodeField
              value={data.billing_code || ''}
              onChange={(code) => {
                onChange({ billing_code: code });
                validateBillingCode(code);
              }}
              error={billingError || null}
            />
          </div>

          <div>
            <label style={labelStyle}>Key Facts</label>
            <HelpText
              collapsible={{
                label: 'Show example',
                content: KEY_FACTS_EXAMPLE,
              }}
            >
              Insert key facts about the company that a journalist would reasonably have access to or know when writing a story.
              This might include CEO and leadership names, a description of the company, and relevant recent events.
              Key facts are provided to journalist agents in all simulations within a project.
            </HelpText>
            <textarea
              value={data.key_facts || ''}
              onChange={(e) => onChange({ key_facts: e.target.value || null })}
              placeholder="Enter key facts about the company..."
              rows={12}
              maxLength={10000}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
            <p style={{ fontSize: '0.75rem', color: theme.colors.text.secondary, marginTop: '2px', textAlign: 'right' }}>
              {(data.key_facts || '').length} / 10,000
            </p>
          </div>

          <div>
            <label style={labelStyle}>Add Members</label>
            <Flex gap="xs" align="center">
              <input
                type="text"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Search users by name or email..."
                style={{ ...inputStyle, flex: 1 }}
              />
            </Flex>
            {userSearch && users && users.length > 0 && (
              <div
                style={{
                  border: `1px solid ${theme.colors.border.default}`,
                  borderRadius: '4px',
                  marginTop: spacing.xs,
                  maxHeight: '150px',
                  overflowY: 'auto',
                }}
              >
                {users
                  .filter((u) => !members.some((m) => m.user_id === u.user_id))
                  .map((u) => (
                    <Flex
                      key={u.user_id}
                      justify="between"
                      align="center"
                      style={{
                        padding: `${spacing.xs} ${spacing.sm}`,
                        borderBottom: `1px solid ${theme.colors.border.default}`,
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        onAddMember(u.user_id);
                        setUserSearch('');
                      }}
                    >
                      <span style={{ fontSize: '0.85rem' }}>{u.name} ({u.email})</span>
                    </Flex>
                  ))}
              </div>
            )}
            {members.length > 0 && (
              <Stack gap="xs" style={{ marginTop: spacing.sm }}>
                {members.map((m) => (
                  <Flex key={m.id} justify="between" align="center">
                    <span style={{ fontSize: '0.85rem' }}>
                      {m.user_name} ({m.user_email})
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      color="error"
                      onClick={() => onRemoveMember(m.id)}
                    >
                      Remove
                    </Button>
                  </Flex>
                ))}
              </Stack>
            )}
          </div>
        </Stack>
      </Card.Body>
    </Card>
  );
}
